import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import NavMenu from '../components/ui/nav-menu';
import FooterNav from '../components/ui/footer-nav';
import ModuleRail from '../components/ui/module-rail';
import HeaderTasksButton from '../components/ui/header-tasks-button';
import { ArrowLeft, FileText, Copy, Check, Loader2, Calculator, Trophy } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DraftScopeResult = () => {
  const navigate = useNavigate();
  const [scopeData, setScopeData] = useState(null);
  const [editedScope, setEditedScope] = useState('');
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showFeatureModal, setShowFeatureModal] = useState(false);

  useEffect(() => {
    // Retrieve the scope result from sessionStorage
    const stored = sessionStorage.getItem('draftScopeResult');
    if (stored) {
      const data = JSON.parse(stored);
      setScopeData(data);
      setEditedScope(data.scope || '');
    } else {
      // No data, redirect back
      navigate('/tradie');
    }
  }, [navigate]);

  const handleCopy = async () => {
    if (!editedScope) return;
    
    // Create a temporary textarea for copying
    const textArea = document.createElement('textarea');
    textArea.value = editedScope;
    
    // Make it invisible but still functional
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.width = '2em';
    textArea.style.height = '2em';
    textArea.style.padding = '0';
    textArea.style.border = 'none';
    textArea.style.outline = 'none';
    textArea.style.boxShadow = 'none';
    textArea.style.background = 'transparent';
    textArea.style.opacity = '0';
    
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    // Also set selection range for iOS
    textArea.setSelectionRange(0, editedScope.length);
    
    let success = false;
    
    try {
      // Try execCommand first (better mobile support)
      success = document.execCommand('copy');
    } catch (err) {
      console.error('execCommand copy failed:', err);
    }
    
    // If execCommand failed, try clipboard API
    if (!success && navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(editedScope);
        success = true;
      } catch (err) {
        console.error('Clipboard API failed:', err);
      }
    }
    
    document.body.removeChild(textArea);
    
    if (success) {
      setCopied(true);
      toast.success('Scope copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error('Could not copy. Try selecting the text and copying manually.');
    }
  };

  const handleDone = async () => {
    // Save the edited scope as a new update
    setSaving(true);
    
    try {
      const payload = {
        job_name: scopeData.title || 'Draft Scope of Works',
        site_address: '',
        update_text: editedScope,
        category: 'Win Work',
        urgency: '',
        required_by: ''
      };

      await axios.post(`${API_URL}/updates`, payload);
      
      toast.success('Scope saved to My Updates');
      sessionStorage.removeItem('draftScopeResult');
      navigate('/tradie/my-updates');
      
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save scope. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!scopeData) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <p className="text-slate-500">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 pb-20 page-enter">
      {/* Header */}
      <header className="bg-[#1F2A37] text-white p-4 shadow-lg sticky top-0 z-10">
        <div className="h-1 bg-emerald-500 absolute top-0 left-0 right-0"></div>
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              data-testid="back-btn"
              onClick={() => navigate('/tradie/capture-job-notes')}
              className="text-white hover:bg-slate-700"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <Trophy className="w-7 h-7 text-emerald-400" />
            <div>
              <h1 className="font-heading text-xl font-bold tracking-tight">
                {scopeData.title || 'Draft Scope'}
              </h1>
              <p className="text-emerald-400 text-xs font-semibold uppercase tracking-wide">WIN WORK</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <HeaderTasksButton stage="win" />
            <NavMenu />
          </div>
        </div>
      </header>

      {/* Module Rail */}
      <ModuleRail stage="win" activeModule="scope" />

      {/* Content */}
      <main className="p-4 max-w-lg mx-auto pb-8">
        <Card className="border-2 border-slate-200 shadow-lg">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-orange-500" />
                Generated Scope
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                data-testid="copy-scope-btn"
                onClick={handleCopy}
                className={copied ? "bg-green-100 border-green-300" : ""}
              >
                {copied ? (
                  <Check className="w-4 h-4 text-green-600" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Editable Scope Content */}
            <Textarea
              data-testid="scope-textarea"
              value={editedScope}
              onChange={(e) => setEditedScope(e.target.value)}
              className="min-h-[320px] text-sm text-slate-700 leading-relaxed bg-slate-50 border-slate-200 resize-y font-sans"
              placeholder="Generated scope will appear here..."
            />

            {/* Status indicator */}
            {scopeData.status === 'success' && (
              <div className="mt-4 flex items-center gap-2 text-green-600 text-sm">
                <Check className="w-4 h-4" />
                <span>Scope generated successfully</span>
              </div>
            )}

            {/* Actions */}
            <div className="mt-6 space-y-3">
              <Button
                data-testid="copy-full-btn"
                onClick={handleCopy}
                variant="outline"
                className="w-full h-12 border-2"
              >
                <Copy className="w-4 h-4 mr-2" />
                {copied ? 'Copied!' : 'Copy Scope Text'}
              </Button>
              
              <Button
                data-testid="done-btn"
                onClick={handleDone}
                disabled={saving}
                className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving Scope...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Save & Done
                  </>
                )}
              </Button>

              {/* Workflow Prompt - Next Step */}
              <button
                type="button"
                data-testid="next-tap-costing-btn"
                onClick={() => setShowFeatureModal(true)}
                className="w-full flex items-center gap-3 p-2.5 rounded-lg border-2 border-dashed border-emerald-300 bg-emerald-50 hover:border-emerald-400 transition-colors"
              >
                <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <Calculator className="w-4 h-4 text-emerald-600" strokeWidth={2} />
                </div>
                <div className="text-left">
                  <span className="text-sm font-semibold text-emerald-600 block">Next tap: Costing</span>
                  <span className="text-xs text-slate-500">Turn this scope into a quick cost estimate</span>
                </div>
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Feature Modal */}
        <Dialog open={showFeatureModal} onOpenChange={setShowFeatureModal}>
          <DialogContent className="sm:max-w-md mx-4 rounded-xl border-2">
            <DialogHeader className="text-left">
              <DialogTitle className="text-lg font-bold text-slate-800">
                This feature can be added to your 2Tap setup
              </DialogTitle>
              <DialogDescription className="text-slate-600 text-sm mt-2">
                Get in touch with the 2Tap team to add this capability.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-2 mt-4">
              <Button
                data-testid="contact-team-btn"
                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold"
                onClick={() => {
                  setShowFeatureModal(false);
                  window.location.href = 'mailto:hello@2tap.com.au?subject=2Tap Feature Request - Costing';
                }}
              >
                Contact 2Tap Team
              </Button>
              <Button
                data-testid="close-modal-btn"
                variant="outline"
                className="w-full font-semibold border-2"
                onClick={() => setShowFeatureModal(false)}
              >
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </main>
      
      <FooterNav />
    </div>
  );
};

export default DraftScopeResult;
